import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { ropaIntakeAPI, tempGuestAPI, entitiesAPI } from '../../services/api';
import { AlertCircle, Loader } from 'lucide-react';

const DATA_SUBJECTS = [
  { value: 'employees_staff', label: 'Employees / staff' },
  { value: 'customers_clients', label: 'Customers / clients' },
  { value: 'job_applicants', label: 'Job applicants' },
  { value: 'contractors_vendors', label: 'Contractors / vendors' },
  { value: 'members_beneficiaries', label: 'Members / beneficiaries' },
  { value: 'website_visitors', label: 'Website visitors' },
  { value: 'students', label: 'Students' },
  { value: 'minors_children', label: 'Minors / children' },
  { value: 'other', label: 'Other (describe below)' },
];

const PERSONAL_DATA_TYPES = [
  { value: 'basic_identity', label: 'Name, ID number, date of birth, nationality', sub: 'Basic identity information' },
  { value: 'contact_details', label: 'Address, phone number, email address', sub: 'Contact details' },
  { value: 'financial_payroll', label: 'Salary, bank account details, tax information', sub: 'Financial and payroll data' },
  { value: 'transactional', label: 'Purchase history, account activity, invoices', sub: 'Transactional records' },
  { value: 'visual_audio', label: 'Photos, CCTV footage, voice recordings', sub: 'Visual or audio data' },
  { value: 'digital_online', label: 'IP address, login data, website behaviour', sub: 'Digital / online activity' },
  { value: 'family_info', label: 'Family status, next of kin, dependants', sub: 'Family information' },
  { value: 'property_details', label: 'Vehicle registration, property ownership', sub: 'Property details' },
  { value: 'immigration_travel', label: 'Passport, visa, travel records', sub: 'Immigration and travel' },
  { value: 'legal_contractual', label: 'Contracts, legal correspondence, court records', sub: 'Legal and contractual' },
  { value: 'other', label: 'Other (describe below)' },
];

const SPECIAL_CATEGORIES = [
  { value: 'health_medical', label: 'Health, medical, or disability information' },
  { value: 'biometric', label: 'Fingerprints, facial recognition, or other biometric data' },
  { value: 'race_ethnicity', label: 'Race or ethnic origin' },
  { value: 'religion', label: 'Religious or philosophical beliefs' },
  { value: 'political_union', label: 'Political opinions or trade union membership' },
  { value: 'sexual_orientation', label: 'Sexual orientation or gender identity' },
  { value: 'criminal_records', label: 'Criminal records or disciplinary history' },
];

const LEGAL_BASIS_OPTIONS = [
  {
    value: 'consent',
    label: 'The person gave us their permission',
    hint: 'e.g. they signed a consent form or ticked a box',
  },
  {
    value: 'contract',
    label: 'It is required to fulfil a contract with the person',
    hint: 'e.g. employment contract, service agreement',
  },
  {
    value: 'legal_obligation',
    label: 'The law or a regulator requires it',
    hint: 'e.g. tax filing, AML reporting, statutory returns',
  },
  {
    value: 'legitimate_interests',
    label: "It is in the organisation's legitimate business interest",
    hint: 'and does not unfairly affect the person',
  },
  {
    value: 'vital_interests',
    label: "It is necessary to protect someone's life",
    hint: '',
  },
  {
    value: 'not_sure',
    label: 'Not sure — DPO to advise',
    hint: '',
  },
];

const THIRD_PARTY_LOCATIONS = [
  { value: 'within_country', label: 'Within this country' },
  { value: 'caribbean', label: 'Within the Caribbean region' },
  { value: 'international', label: 'Outside the Caribbean (international)' },
  { value: 'mixed', label: 'Mix of locations — described above' },
];

const SYSTEM_HOSTING_OPTIONS = [
  { value: 'on_premises', label: 'On our own servers (on-premises)' },
  { value: 'cloud', label: 'Cloud / hosted externally by a third-party provider' },
  { value: 'not_sure', label: 'Not sure' },
];

const DATA_SOURCES = [
  { value: 'directly', label: 'Directly from the person', hint: 'e.g. they fill in a form, attend an interview, or contact us' },
  { value: 'internal', label: 'From another department within the organisation', hint: '' },
  { value: 'external', label: 'From an external source', hint: 'e.g. a government agency, credit bureau, recruitment agency, or data provider' },
];

function SectionHeader({ number, title }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-7 h-7 rounded flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ backgroundColor: '#10b981' }}>
        {number}
      </div>
      <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
    </div>
  );
}

function CheckboxGroup({ options, selected, onChange, cols = 2 }) {
  const toggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className={`grid grid-cols-1 ${cols === 2 ? 'sm:grid-cols-2' : ''} gap-2`}>
      {options.map(opt => (
        <label key={opt.value} className="flex items-start gap-2.5 cursor-pointer group">
          <Checkbox
            checked={selected.includes(opt.value)}
            onCheckedChange={() => toggle(opt.value)}
            className="mt-0.5 flex-shrink-0"
          />
          <div>
            <span className="text-sm text-slate-800 dark:text-slate-200 group-hover:text-slate-900 dark:group-hover:text-white">
              {opt.label}
            </span>
            {opt.sub && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{opt.sub}</p>
            )}
            {opt.hint && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 italic">{opt.hint}</p>
            )}
          </div>
        </label>
      ))}
    </div>
  );
}

function RadioGroup({ options, selected, onChange }) {
  return (
    <div className="space-y-3">
      {options.map(opt => (
        <label key={opt.value} className="flex items-start gap-2.5 cursor-pointer group">
          <div className="mt-0.5 flex-shrink-0">
            <div
              className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                selected === opt.value
                  ? 'border-emerald-500 bg-emerald-500'
                  : 'border-slate-400 dark:border-slate-600'
              }`}
              onClick={() => onChange(opt.value)}
            >
              {selected === opt.value && (
                <div className="w-1.5 h-1.5 rounded-full bg-white" />
              )}
            </div>
          </div>
          <div onClick={() => onChange(opt.value)}>
            <span className="text-sm text-slate-800 dark:text-slate-200">{opt.label}</span>
            {opt.hint && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 italic">{opt.hint}</p>
            )}
          </div>
        </label>
      ))}
    </div>
  );
}

const INITIAL_FORM = {
  // Section 1
  submitter_name: '',
  submitter_job_title: '',
  submitter_email: '',
  submitter_unit: '',
  submitter_department: '',
  // Section 2
  process_name: '',
  purpose: '',
  // Section 3
  data_subjects: [],
  data_subjects_other: '',
  // Section 4
  personal_data_types: [],
  personal_data_types_other: '',
  collects_sensitive_data: false,
  sensitive_data_types: [],
  sensitive_data_none: false,
  // Section 5
  legal_basis: '',
  // Section 6
  data_collection_sources: [],
  data_collection_source_detail: '',
  // Section 7
  retention_period: '',
  retention_reason: '',
  // Section 8
  shares_internally: '',       // 'yes' | 'no' | ''
  internal_sharing_areas: '',
  shares_externally: '',       // 'yes' | 'no' | ''
  external_sharing_recipients: '',
  third_party_locations: [],
  // Section 9
  uses_system: '',             // 'yes' | 'no' | ''
  system_name: '',
  system_hosting: '',
  // Section 10
  additional_notes: '',
  // Declaration
  declaration_name: '',
  declaration_date: '',
  declaration_signature: '',
  // Legacy
  record_types: ['digital'],
  electronic_security_measures: [],
  transfer_safeguards: '',
};

export default function ROPAIntakeFormPage({ guestMode = false }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const guestUser = guestMode ? JSON.parse(localStorage.getItem('guest_user') || '{}') : null;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validation, setValidation] = useState(null);
  const [guestEntityName, setGuestEntityName] = useState('');
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState(INITIAL_FORM);

  useEffect(() => {
    if (guestMode) {
      if (guestUser) {
        setFormData(prev => ({
          ...prev,
          submitter_name: guestUser.full_name || '',
          submitter_email: guestUser.email || '',
          submitter_department: guestUser.department || '',
        }));
        if (guestUser.entity_id) {
          entitiesAPI.getOne(guestUser.entity_id)
            .then(res => setGuestEntityName(res.data?.entity_name || ''))
            .catch(() => {});
        }
      }
      setLoading(false);
      return;
    }
    validateToken();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateToken = async () => {
    try {
      const response = await ropaIntakeAPI.validateToken(token);
      setValidation(response.data);
      if (response.data.department) {
        setFormData(prev => ({ ...prev, submitter_department: response.data.department }));
      }
      setLoading(false);
      if (!response.data.is_valid) {
        setError(`Token is ${response.data.token_status}`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired token');
      setLoading(false);
    }
  };

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const validate = () => {
    if (!formData.submitter_name.trim()) {
      toast.error('Your name is required');
      return false;
    }
    if (!formData.submitter_job_title.trim()) {
      toast.error('Your job title is required');
      return false;
    }
    if (!formData.submitter_email.trim()) {
      toast.error('Your email address is required');
      return false;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(formData.submitter_email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!formData.process_name.trim()) {
      toast.error('Activity name is required');
      return false;
    }
    if (!formData.purpose.trim()) {
      toast.error('Purpose of processing is required');
      return false;
    }
    if (formData.data_subjects.length === 0) {
      toast.error('Please select at least one type of data subject');
      return false;
    }
    if (formData.personal_data_types.length === 0) {
      toast.error('Please select at least one type of personal information');
      return false;
    }
    if (!formData.legal_basis) {
      toast.error('Please select a legal basis');
      return false;
    }
    if (formData.data_collection_sources.length === 0) {
      toast.error('Please select how the information is collected');
      return false;
    }
    if (!formData.retention_period.trim()) {
      toast.error('Retention period is required');
      return false;
    }
    if (!formData.shares_internally) {
      toast.error('Please indicate whether information is shared internally');
      return false;
    }
    if (!formData.shares_externally) {
      toast.error('Please indicate whether information is shared externally');
      return false;
    }
    if (!formData.uses_system) {
      toast.error('Please indicate whether a system or software is used');
      return false;
    }
    if (!formData.declaration_name.trim()) {
      toast.error('Please enter your name in the declaration');
      return false;
    }
    if (!formData.declaration_date) {
      toast.error('Please enter the date in the declaration');
      return false;
    }
    return true;
  };

  const buildPayload = () => ({
    submitter_name: formData.submitter_name.trim(),
    submitter_job_title: formData.submitter_job_title.trim(),
    submitter_email: formData.submitter_email.trim(),
    submitter_unit: formData.submitter_unit.trim() || null,
    submitter_department: formData.submitter_department.trim() || null,
    process_name: formData.process_name.trim(),
    purpose: formData.purpose.trim(),
    data_subjects: formData.data_subjects,
    personal_data_types: formData.personal_data_types,
    collects_sensitive_data: formData.collects_sensitive_data,
    sensitive_data_types: formData.collects_sensitive_data ? formData.sensitive_data_types : [],
    legal_basis: formData.legal_basis,
    data_collection_sources: formData.data_collection_sources,
    data_collection_source_detail: formData.data_collection_sources.includes('external')
      ? formData.data_collection_source_detail.trim() || null
      : null,
    retention_period: formData.retention_period.trim(),
    retention_reason: formData.retention_reason.trim() || null,
    shares_internally: formData.shares_internally === 'yes',
    internal_sharing_areas: formData.shares_internally === 'yes'
      ? formData.internal_sharing_areas.trim() || null
      : null,
    shares_externally: formData.shares_externally === 'yes',
    external_sharing_recipients: formData.shares_externally === 'yes'
      ? formData.external_sharing_recipients.trim() || null
      : null,
    third_party_locations: formData.shares_externally === 'yes' ? formData.third_party_locations : [],
    transfer_safeguards: formData.transfer_safeguards.trim() || null,
    uses_system: formData.uses_system === 'yes',
    system_name: formData.uses_system === 'yes' ? formData.system_name.trim() || null : null,
    system_hosting: formData.uses_system === 'yes' ? formData.system_hosting || null : null,
    additional_notes: formData.additional_notes.trim() || null,
    declaration_name: formData.declaration_name.trim(),
    declaration_date: formData.declaration_date,
    declaration_signature: formData.declaration_signature.trim() || null,
    record_types: formData.record_types,
    electronic_security_measures: formData.electronic_security_measures,
  });

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      setSubmitting(true);
      const payload = buildPayload();
      let response;
      if (guestMode) {
        response = await tempGuestAPI.submitRopa(payload);
        toast.success('Record submitted successfully');
        navigate('/guest/portal');
      } else {
        response = await ropaIntakeAPI.submit(token, payload);
        toast.success('Record submitted successfully');
        navigate(`/ropa-intake/${token}/success`, {
          state: {
            reference: response.data.reference_number,
            submitterName: response.data.submitter_name,
            submitterEmail: response.data.submitter_email,
            department: response.data.department,
          },
        });
      }
    } catch (err) {
      console.error('Submission failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin mx-auto mb-2" />
          <p>Loading form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <CardTitle>Invalid Link</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate('/')} className="w-full">Back to Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const entityName = guestMode ? guestEntityName : validation?.entity_name;
  const department = guestMode ? guestUser?.department : validation?.department;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-10 px-4">
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
            Assura · Data Mapping Intake
          </p>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-5">
            Processing Activity Questionnaire
          </h1>
          {/* Guidance box matching PDF teal border */}
          <div className="rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 px-4 py-3 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            Complete one form per business activity that involves collecting, storing, using, or sharing personal
            information. If your department has multiple activities, you will receive a separate form for each one.
            Your responses will be reviewed by your Data Protection Officer before being added to the
            organisation's compliance records.
          </div>
          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            Fields marked <span className="text-red-500 font-bold">*</span> are required. Provide your best
            estimate where you are unsure of exact details — your DPO will follow up if clarification is needed.
          </p>
        </div>

        {/* Context bar */}
        {(entityName || department) && (
          <div className="mb-6 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-3 flex flex-wrap gap-6 text-sm">
            {entityName && (
              <div>
                <span className="text-xs text-slate-500 dark:text-slate-400 block mb-0.5">Organisation</span>
                <span className="font-medium text-slate-800 dark:text-slate-200">{entityName}</span>
              </div>
            )}
            {department && (
              <div>
                <span className="text-xs text-slate-500 dark:text-slate-400 block mb-0.5">Department</span>
                <span className="font-medium text-slate-800 dark:text-slate-200">{department}</span>
              </div>
            )}
          </div>
        )}

        {/* ── Section 1: About your department ── */}
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6 mb-5">
          <SectionHeader number="1" title="About your department" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="dept">Department <span className="text-red-500">*</span></Label>
              <Input
                id="dept"
                placeholder="Your department"
                value={formData.submitter_department}
                disabled={!!department}
                onChange={e => set('submitter_department', e.target.value)}
                className={`mt-1 ${department ? 'bg-slate-50 dark:bg-slate-800' : ''}`}
              />
            </div>
            <div>
              <Label htmlFor="unit">Unit or team <span className="text-xs text-slate-400 font-normal">(if applicable)</span></Label>
              <Input
                id="unit"
                placeholder="e.g. Finance Operations"
                value={formData.submitter_unit}
                onChange={e => set('submitter_unit', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="s_name">Your name <span className="text-red-500">*</span></Label>
              <Input
                id="s_name"
                placeholder="Full name"
                value={formData.submitter_name}
                onChange={e => set('submitter_name', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="s_title">Your job title <span className="text-red-500">*</span></Label>
              <Input
                id="s_title"
                placeholder="e.g. HR Manager"
                value={formData.submitter_job_title}
                onChange={e => set('submitter_job_title', e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="s_email">
                Your email address <span className="text-red-500">*</span>
              </Label>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">The DPO may contact you to clarify your responses.</p>
              <Input
                id="s_email"
                type="email"
                placeholder="you@organisation.com"
                value={formData.submitter_email}
                onChange={e => set('submitter_email', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ── Section 2: The activity ── */}
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6 mb-5">
          <SectionHeader number="2" title="The activity" />
          <div className="space-y-4">
            <div>
              <Label htmlFor="proc_name">
                What is the name of this activity? <span className="text-red-500">*</span>
              </Label>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                Use a short descriptive name. Examples: Employee payroll processing, Customer onboarding, CCTV monitoring.
              </p>
              <Input
                id="proc_name"
                placeholder="Activity name"
                value={formData.process_name}
                onChange={e => set('process_name', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="purpose">
                What is this activity for? <span className="text-red-500">*</span>
              </Label>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                Describe in plain language why your department carries out this activity and what it achieves.
              </p>
              <Textarea
                id="purpose"
                placeholder="Describe the purpose..."
                value={formData.purpose}
                onChange={e => set('purpose', e.target.value)}
                rows={4}
              />
            </div>
          </div>
        </div>

        {/* ── Section 3: Whose information is involved ── */}
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6 mb-5">
          <SectionHeader number="3" title="Whose information is involved?" />
          <div className="mb-3">
            <p className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-1">
              Which of the following people does this activity collect or use information about? <span className="text-red-500">*</span>
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">Select all that apply.</p>
            <CheckboxGroup
              options={DATA_SUBJECTS}
              selected={formData.data_subjects}
              onChange={v => set('data_subjects', v)}
            />
          </div>
          {formData.data_subjects.includes('other') && (
            <div className="mt-3">
              <Label htmlFor="ds_other">If other, describe here</Label>
              <Input
                id="ds_other"
                placeholder="Describe other data subjects..."
                value={formData.data_subjects_other}
                onChange={e => set('data_subjects_other', e.target.value)}
                className="mt-1"
              />
            </div>
          )}
        </div>

        {/* ── Section 4: What information is collected ── */}
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6 mb-5">
          <SectionHeader number="4" title="What information is collected?" />

          <div className="mb-5">
            <p className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-1">
              What types of personal information does this activity involve? <span className="text-red-500">*</span>
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              Select all that apply. Do not skip categories — under-reporting is a common compliance gap.
            </p>
            <CheckboxGroup
              options={PERSONAL_DATA_TYPES}
              selected={formData.personal_data_types}
              onChange={v => set('personal_data_types', v)}
              cols={1}
            />
            {formData.personal_data_types.includes('other') && (
              <div className="mt-3">
                <Label htmlFor="pdt_other">If other, describe here</Label>
                <Input
                  id="pdt_other"
                  placeholder="Describe other data types..."
                  value={formData.personal_data_types_other}
                  onChange={e => set('personal_data_types_other', e.target.value)}
                  className="mt-1"
                />
              </div>
            )}
          </div>

          {/* Special categories */}
          <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 px-4 py-3 mb-4 text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
            Special category information — the following types attract a higher level of legal protection under
            data protection law. Only declare what this activity actually collects. Your DPO will follow up if
            any apply.
          </div>
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-3">
            Does this activity involve any of the following?
            <span className="text-xs text-slate-500 font-normal ml-2">Select all that apply.</span>
          </p>
          <CheckboxGroup
            options={SPECIAL_CATEGORIES}
            selected={formData.sensitive_data_types}
            onChange={v => {
              set('sensitive_data_types', v);
              set('collects_sensitive_data', v.length > 0);
              if (v.length > 0) set('sensitive_data_none', false);
            }}
            cols={1}
          />
          <label className="flex items-center gap-2.5 cursor-pointer mt-2">
            <Checkbox
              checked={formData.sensitive_data_none}
              onCheckedChange={checked => {
                set('sensitive_data_none', checked);
                if (checked) {
                  set('sensitive_data_types', []);
                  set('collects_sensitive_data', false);
                }
              }}
            />
            <span className="text-sm text-slate-800 dark:text-slate-200">None of the above</span>
          </label>
        </div>

        {/* ── Section 5: Legal basis ── */}
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6 mb-5">
          <SectionHeader number="5" title="Why does the organisation have the right to use this information?" />
          <div className="rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 px-4 py-3 mb-4 text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
            Your DPO will confirm the correct legal basis. Select the option that best describes why people allow
            you to use their information.
          </div>
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-3">
            Which best describes this activity? <span className="text-red-500">*</span>
          </p>
          <RadioGroup
            options={LEGAL_BASIS_OPTIONS}
            selected={formData.legal_basis}
            onChange={v => set('legal_basis', v)}
          />
        </div>

        {/* ── Section 6: Where does the information come from ── */}
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6 mb-5">
          <SectionHeader number="6" title="Where does the information come from?" />
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-1">
            How is the information collected? <span className="text-red-500">*</span>
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">Select all that apply.</p>
          <CheckboxGroup
            options={DATA_SOURCES}
            selected={formData.data_collection_sources}
            onChange={v => set('data_collection_sources', v)}
            cols={1}
          />
          {formData.data_collection_sources.includes('external') && (
            <div className="mt-4">
              <Label htmlFor="ext_source">If from an external source, who provides it and why?</Label>
              <Textarea
                id="ext_source"
                placeholder="e.g. Credit bureau provides credit scores for loan applications."
                value={formData.data_collection_source_detail}
                onChange={e => set('data_collection_source_detail', e.target.value)}
                rows={3}
                className="mt-1"
              />
            </div>
          )}
        </div>

        {/* ── Section 7: Retention ── */}
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6 mb-5">
          <SectionHeader number="7" title="How long is the information kept?" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ret_period">
                How long do you keep this information? <span className="text-red-500">*</span>
              </Label>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                Give your best estimate if you are unsure of the exact period.
              </p>
              <Input
                id="ret_period"
                placeholder="e.g. 7 years, 2 years after contract ends"
                value={formData.retention_period}
                onChange={e => set('retention_period', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="ret_reason">Why is it kept for that period?</Label>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                e.g. Legal requirement, company policy
              </p>
              <Input
                id="ret_reason"
                placeholder="Reason for retention period"
                value={formData.retention_reason}
                onChange={e => set('retention_reason', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ── Section 8: Sharing ── */}
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6 mb-5">
          <SectionHeader number="8" title="Who else sees or receives this information?" />

          {/* Internal sharing */}
          <div className="mb-5">
            <p className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-2">
              Is this information shared with other departments internally? <span className="text-red-500">*</span>
            </p>
            <div className="flex gap-6">
              {['yes', 'no'].map(v => (
                <label key={v} className="flex items-center gap-2 cursor-pointer">
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                      formData.shares_internally === v
                        ? 'border-emerald-500 bg-emerald-500'
                        : 'border-slate-400 dark:border-slate-600'
                    }`}
                    onClick={() => set('shares_internally', v)}
                  >
                    {formData.shares_internally === v && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                  <span className="text-sm capitalize text-slate-800 dark:text-slate-200" onClick={() => set('shares_internally', v)}>
                    {v === 'yes' ? 'Yes' : 'No'}
                  </span>
                </label>
              ))}
            </div>
            {formData.shares_internally === 'yes' && (
              <div className="mt-3">
                <Label htmlFor="int_sharing">If yes — which departments and for what reason?</Label>
                <Textarea
                  id="int_sharing"
                  placeholder="e.g. Finance team — receives payroll data for salary processing"
                  value={formData.internal_sharing_areas}
                  onChange={e => set('internal_sharing_areas', e.target.value)}
                  rows={3}
                  className="mt-1"
                />
              </div>
            )}
          </div>

          {/* External sharing */}
          <div>
            <p className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-2">
              Is this information shared with any external organisations or third parties? <span className="text-red-500">*</span>
            </p>
            <div className="flex gap-6">
              {['yes', 'no'].map(v => (
                <label key={v} className="flex items-center gap-2 cursor-pointer">
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                      formData.shares_externally === v
                        ? 'border-emerald-500 bg-emerald-500'
                        : 'border-slate-400 dark:border-slate-600'
                    }`}
                    onClick={() => set('shares_externally', v)}
                  >
                    {formData.shares_externally === v && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                  <span className="text-sm capitalize text-slate-800 dark:text-slate-200" onClick={() => set('shares_externally', v)}>
                    {v === 'yes' ? 'Yes' : 'No'}
                  </span>
                </label>
              ))}
            </div>
            {formData.shares_externally === 'yes' && (
              <div className="mt-3 space-y-4">
                <div>
                  <Label htmlFor="ext_recipients">
                    If yes — list each third party, what information they receive, and why
                  </Label>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-1 italic">
                    Example: ADP Caribbean — receives salary and deduction data to process payroll payments.
                  </p>
                  <Textarea
                    id="ext_recipients"
                    placeholder="List third parties..."
                    value={formData.external_sharing_recipients}
                    onChange={e => set('external_sharing_recipients', e.target.value)}
                    rows={4}
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-2">
                    Where are these third parties based?
                  </p>
                  <CheckboxGroup
                    options={THIRD_PARTY_LOCATIONS}
                    selected={formData.third_party_locations}
                    onChange={v => set('third_party_locations', v)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Section 9: Systems used ── */}
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6 mb-5">
          <SectionHeader number="9" title="Systems used" />
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-2">
            Is any software, system, or application used for this activity? <span className="text-red-500">*</span>
          </p>
          <div className="flex gap-6 mb-4">
            {['yes', 'no'].map(v => (
              <label key={v} className="flex items-center gap-2 cursor-pointer">
                <div
                  className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                    formData.uses_system === v
                      ? 'border-emerald-500 bg-emerald-500'
                      : 'border-slate-400 dark:border-slate-600'
                  }`}
                  onClick={() => set('uses_system', v)}
                >
                  {formData.uses_system === v && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                </div>
                <span className="text-sm text-slate-800 dark:text-slate-200" onClick={() => set('uses_system', v)}>
                  {v === 'yes' ? 'Yes' : 'No'}
                </span>
              </label>
            ))}
          </div>
          {formData.uses_system === 'yes' && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="sys_name">If yes — name of system or software</Label>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-1 italic">
                  e.g. SAP, Salesforce, Microsoft 365, custom internal system
                </p>
                <Input
                  id="sys_name"
                  placeholder="System or software name"
                  value={formData.system_name}
                  onChange={e => set('system_name', e.target.value)}
                />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-2">
                  Where is this system hosted?
                </p>
                <RadioGroup
                  options={SYSTEM_HOSTING_OPTIONS}
                  selected={formData.system_hosting}
                  onChange={v => set('system_hosting', v)}
                />
              </div>
            </div>
          )}
        </div>

        {/* ── Section 10: Anything else ── */}
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6 mb-5">
          <SectionHeader number="10" title="Anything else" />
          <Label htmlFor="notes">Is there anything else about this activity you think the DPO should know?</Label>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
            For example: recent changes to how information is handled, known issues, or concerns about this process.
          </p>
          <Textarea
            id="notes"
            placeholder="Optional additional information..."
            value={formData.additional_notes}
            onChange={e => set('additional_notes', e.target.value)}
            rows={4}
          />
        </div>

        {/* ── Declaration ── */}
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6 mb-8">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-2">Declaration</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            I confirm that the information provided in this form is accurate to the best of my knowledge. I
            understand that this submission will be reviewed by the Data Protection Officer and used to maintain
            the organisation's record of processing activities.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <Label htmlFor="decl_name">Name <span className="text-red-500">*</span></Label>
              <Input
                id="decl_name"
                placeholder="Your full name"
                value={formData.declaration_name}
                onChange={e => set('declaration_name', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="decl_date">Date <span className="text-red-500">*</span></Label>
              <Input
                id="decl_date"
                type="date"
                value={formData.declaration_date}
                onChange={e => set('declaration_date', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="decl_sig">Signature</Label>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
              Type your full name below to serve as your electronic signature.
            </p>
            <Input
              id="decl_sig"
              placeholder="Type your full name"
              value={formData.declaration_signature}
              onChange={e => set('declaration_signature', e.target.value)}
              className="mt-1 font-serif italic"
            />
          </div>
        </div>

        {/* Submit */}
        <div className="flex gap-3 mb-10">
          <Button variant="outline" onClick={() => navigate('/')} className="flex-1" disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} className="flex-1">
            {submitting ? 'Submitting...' : 'Submit Form'}
          </Button>
        </div>

        <p className="text-xs text-slate-400 text-center mb-8">
          Your information is secure and will only be used to process this ROPA record submission.
        </p>
      </div>
    </div>
  );
}
